import { QueryClient } from "@tanstack/react-query";

// Client-facing records can contain joined names and recipient defaults.
// Refresh their reads; never rewrite the historical messages themselves.
const CLIENT_DEPENDENT_RESOURCES = [
  "contacts",
  "contacts_summary",
  "companies",
  "companies_summary",
  "tickets",
  "client_invoices",
  "client_subscriptions",
  "client_proposals",
  "deals",
  "conversations",
  "ticket-client-conversation",
];

const RESOURCE_INVALIDATION_MAP: Record<string, string[]> = {
  contacts: CLIENT_DEPENDENT_RESOURCES,
  companies: CLIENT_DEPENDENT_RESOURCES,
  tasks: [
    "tasks",
    "scopedTasks",
    "task_participants",
    "task_tag_notifications",
  ],
  task_participants: ["task_participants", "scopedTasks", "tasks"],
  task_tag_notifications: ["task_tag_notifications", "tasks"],
  deals: ["deals", "scopedTasks", "myProjectDealIds"],
  organization_members: ["organization_members"],
  conversations: ["conversations"],
  conversation_messages: ["conversations", "conversation_messages"],
  tickets: ["tickets"],
  ticket_messages: ["ticket_messages", "tickets"],
  client_invoices: ["client_invoices", "tickets"],
  client_subscriptions: ["client_subscriptions", "client_invoices"],
  ticket_deliverables: ["ticket_deliverables", "tickets"],
};

export const createCrmQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 10_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        networkMode: "online",
      },
      mutations: {
        networkMode: "online",
      },
    },
  });

/** Sources of the `client_activity_timeline` view. */
const TIMELINE_SOURCE_RESOURCES = new Set([
  "contacts",
  "companies",
  "contact_notes",
  "deal_notes",
  "tasks",
  "calendar_events",
  "tickets",
  "ticket_messages",
  "conversations",
  "conversation_messages",
  "voice_calls",
  "proposals",
  "client_invoices",
  "contracts",
  "deals",
  "client_subscriptions",
]);

export const invalidateQueriesForResource = async (
  queryClient: QueryClient,
  resource: string,
) => {
  const keys = [...(RESOURCE_INVALIDATION_MAP[resource] ?? [resource])];
  if (TIMELINE_SOURCE_RESOURCES.has(resource)) {
    keys.push("client_activity_timeline");
  }

  await Promise.all(
    keys.map((queryKey) =>
      queryClient.invalidateQueries({
        queryKey: [queryKey],
        refetchType: "active",
      }),
    ),
  );
};
